import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"; // Added useMemo
import { Chart, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import Footer from "../../components/Footer";
import FooterItem from "../../components/FooterItem";
import SelectionPills from "../components/SelectionPill"; // Fixed import name
import {
	butaniData,
	getPhototherapyData,
	getRiskZone,
	shouldUsePhototherapy,
	gerTransfusionResult,
	getGenericCaseIndex,
	phototherapyChartsTitleSuffix,
	getPhototherapyDataPointsByCase,
	getTransfusionDataPointsByCase,
	getYOnCurveByX,
} from "./calculation.ts"; // Added .ts extension
import "./phototherapy.scss"; // Import the CSS file
import { FaInfoCircle } from "react-icons/fa"; // Using react-icons for icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Import FontAwesomeIcon
import {
	faArrowRightLong,
	faBedPulse,
	faChartLine,
	faDroplet,
	faChartGantt,
	faBaby,
} from "@fortawesome/free-solid-svg-icons";
import Image from "../components/Image";
import AppsHeader from "../components/AppsHeader";
import { Alert, Card, CardBody, Checkbox, Input } from "@heroui/react";

// Register Chart.js components and plugins
Chart.register(...registerables, annotationPlugin);

const labelStyle = {
	width: "100px",
	minWidth: "100px",
	textAlign: "right" as const,
	color: "#0C3058",
	alignSelf: "center",
};

type WeekOfBirth = "above38" | "under38";
type DataShown =
	| "CALCULATOR"
	| "RISKS"
	| "PHOTOTHERAPY_GRAPH"
	| "GRAPH_BUTANI"
	| "EXCHANGE_TRANSFUSION_GRAPH";

// Define the structure for risk zone object
interface RiskZone {
	riskZone: number;
	percentileString: string;
	explanation: string;
}

interface PhototherapyIsraeliCurvesProps {
	/**
	 * The name of the app this calculator is mounted under, for the breadcrumb —
	 * two apps share this screen (see `variants.ts`). The header below names the
	 * calculator, which is the same in both.
	 */
	pageTitle?: string;
}

export const PhototherapyIsraeliCurves: React.FC<PhototherapyIsraeliCurvesProps> = ({
	pageTitle = "צהבת ביילוד",
}) => {
	const [weekOfBirth, setWeekOfBirth] = useState<WeekOfBirth>("above38");
	const [hasRiskFactors, setHasRiskFactors] = useState<boolean>(false);
	const [bilirubin, setBilirubin] = useState<string>("");
	const [ageInHours, setAgeInHours] = useState<string>("");

	const [rootDiagnose, setRootDiagnose] = useState<string>("");
	const [lightAlertMessage, setLightAlertMessage] = useState<string>("");
	const [considerTransfusion, setConsiderTransfusion] = useState<string>("");
	const [riskZoneObj, setRiskZoneObj] = useState<Partial<RiskZone>>({});
	const [statusColor, setStatusColor] = useState<string>(""); // 'red' or 'green'
	const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
	const [dataShown, setDataShown] = useState<DataShown>("CALCULATOR");
	// Removed unused imagePath38 and imagePath37 state variables

	const phototherapyChartRef = useRef<Chart<"line">>(null);
	const butaniChartRef = useRef<Chart<"line">>(null);

	const chartsContainerRef = useRef<HTMLDivElement>(null); // scroll to charts

	const isWeek38Plus = weekOfBirth === "above38";
	const bilirubinNum = parseFloat(bilirubin.replace(",", "."));
	const ageInHoursNum = parseInt(ageInHours, 10);

	const allInputsSatisfied =
		!isNaN(ageInHoursNum) && ageInHoursNum > 0 && !isNaN(bilirubinNum) && bilirubinNum > 0;

	const calculateResults = useCallback(() => {
		if (!allInputsSatisfied) {
			setRootDiagnose("");
			setLightAlertMessage("");
			setConsiderTransfusion("");
			setRiskZoneObj({});
			setStatusColor("");
			setIsCollapsed(true); // Collapse graphs when inputs are invalid
			return;
		}

		const { shouldUse, delta } = shouldUsePhototherapy(
			ageInHoursNum,
			bilirubinNum,
			isWeek38Plus,
			hasRiskFactors
		);
		setRootDiagnose(shouldUse ? "נדרש טיפול באור" : "לא נדרש טיפול באור");
		setLightAlertMessage(!shouldUse && delta <= 0.5 ? "ערך בילירובין מתקרב לסף טיפול באור" : "");
		setConsiderTransfusion(
			gerTransfusionResult(ageInHoursNum, bilirubinNum, isWeek38Plus, hasRiskFactors)
		);
		setStatusColor(shouldUse ? "red" : "green");

		if (ageInHoursNum >= 12) {
			const newRiskZoneObj = getRiskZone(ageInHoursNum, bilirubinNum, hasRiskFactors, shouldUse);
			setRiskZoneObj(newRiskZoneObj);
		} else {
			setRiskZoneObj({});
		}

		// Keep graphs collapsed unless explicitly opened
		// setIsCollapsed(false); // Don't automatically expand
	}, [ageInHoursNum, bilirubinNum, isWeek38Plus, hasRiskFactors, allInputsSatisfied]);

	// Recalculate when inputs change (with debounce?)
	// Using useEffect for calculation based on state changes
	useEffect(() => {
		// Simple debounce mechanism
		const handler = setTimeout(
			() => {
				calculateResults();
			},
			ageInHoursNum <= 9 && ageInHours.length === 1 ? 800 : 20
		); // Delay for single-digit age

		return () => {
			clearTimeout(handler);
		};
	}, [calculateResults, ageInHours, ageInHoursNum]); // Add ageInHours to dependencies for debounce logic

	// Scroll to charts when they become visible, works on mobile too
	useEffect(() => {
		if (!isCollapsed && allInputsSatisfied) {
			const timer = setTimeout(() => {
				chartsContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 100); // Small delay to ensure the chart container is rendered
			return () => clearTimeout(timer);
		}
	}, [isCollapsed, allInputsSatisfied]);

	const resetAll = () => {
		setWeekOfBirth("above38");
		setHasRiskFactors(false);
		setBilirubin("");
		setAgeInHours("");
		// Clear results
		setRootDiagnose("");
		setLightAlertMessage("");
		setConsiderTransfusion("");
		setRiskZoneObj({});
		setStatusColor("");
		setIsCollapsed(true);
		setDataShown("CALCULATOR");
		// Removed reset for unused image paths
	};

	const handleBilirubinChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			// Allow only numbers and one decimal point (comma or dot)
			if (/^[0-9]{0,2}([.,][0-9]{0,1})?$/.test(value) || value === "") {
				// Basic validation, more robust needed for max value etc.
				if (
					parseFloat(value.replace(",", ".")) <= 50 ||
					value === "" ||
					value.endsWith(".") ||
					value.endsWith(",")
				) {
					setBilirubin(value);
				}
			}
		},
		[setBilirubin]
	); // Dependency: setBilirubin (stable)

	const handleAgeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			// Allow only numbers up to 3 digits
			if (/^[0-9]{0,3}$/.test(value) || value === "") {
				setAgeInHours(value);
			}
		},
		[setAgeInHours]
	); // Dependency: setAgeInHours (stable)

	const riskZoneSatisfied = Object.keys(riskZoneObj).length > 0;
	const isFollowUpRelevant =
		riskZoneObj.riskZone === 3 ||
		riskZoneObj.riskZone === 4 ||
		(riskZoneObj.riskZone === 2 && !hasRiskFactors);
	const shouldShowFollowUp =
		riskZoneSatisfied && isFollowUpRelevant && rootDiagnose !== "נדרש טיפול באור";

	// Thresholds at the patient's current age — for the explanatory line under the result.
	const calculatedThresholds = useMemo(() => {
		if (!allInputsSatisfied) return null;

		const photoThreshold =
			ageInHoursNum < 6
				? ageInHoursNum / 2
				: getYOnCurveByX(
						getPhototherapyDataPointsByCase(isWeek38Plus, hasRiskFactors),
						ageInHoursNum
				  );

		const transfusionThreshold =
			ageInHoursNum < 6
				? NaN
				: getYOnCurveByX(
						getTransfusionDataPointsByCase(isWeek38Plus, hasRiskFactors),
						ageInHoursNum
				  );

		return { photo: photoThreshold, transfusion: transfusionThreshold };
	}, [allInputsSatisfied, ageInHoursNum, isWeek38Plus, hasRiskFactors]);

	const formatThresholdNum = (val: number) => Number(val.toFixed(1)).toString();

	// --- Memoized Chart Data and Options ---

	const phototherapyChartData = useMemo(() => {
		return getPhototherapyData(isWeek38Plus, hasRiskFactors);
	}, [isWeek38Plus, hasRiskFactors]);

	const butaniChartData = butaniData; // This is constant, no need to memoize the data itself

	// Memoize point datasets
	const phototherapyPointDataset = useMemo(() => {
		return allInputsSatisfied
			? [
					{
						data: [{ x: ageInHoursNum, y: bilirubinNum }],
						backgroundColor: "red", // Different color for the point
						borderColor: "red",
						pointRadius: 3,
						pointHoverRadius: 5,
						label: "הערך הנוכחי",
					},
			  ]
			: [];
	}, [allInputsSatisfied, ageInHoursNum, bilirubinNum]);

	const butaniPointDataset = useMemo(() => {
		return allInputsSatisfied
			? [
					{
						data: [{ x: ageInHoursNum, y: bilirubinNum }],
						backgroundColor: "red", // Different color for the point
						borderColor: "red",
						pointRadius: 3,
						pointHoverRadius: 5,
						label: "הערך הנוכחי",
					},
			  ]
			: [];
	}, [allInputsSatisfied, ageInHoursNum, bilirubinNum]);

	// Memoize final data structures for charts
	const phototherapyFinalData = useMemo(
		() => ({
			datasets: [...phototherapyChartData.datasets, ...phototherapyPointDataset],
		}),
		[phototherapyChartData, phototherapyPointDataset]
	);

	const butaniFinalData = useMemo(
		() => ({
			labels: butaniChartData.labels, // Ensure labels are passed
			datasets: [...butaniChartData.datasets, ...butaniPointDataset],
		}),
		[butaniChartData, butaniPointDataset]
	); // butaniChartData is stable

	// Memoize chart options
	const phototherapyChartOptions = useMemo(() => {
		const commonChartOptions = {
			// Define common options inside useMemo or make it a separate memoized value if reused heavily
			responsive: true,
			aspectRatio: 1,
			plugins: {
				legend: {
					display: true,
					labels: {
						filter: (legendItem: any) =>
							legendItem.datasetIndex < 2 ||
							(legendItem.datasetIndex === phototherapyFinalData.datasets.length - 1 &&
								allInputsSatisfied), // Show base lines and point if valid
						usePointStyle: true,
						pointStyle: "line",
					},
					position: "top" as const,
				},
				tooltip: {
					rtl: true,
					textDirection: "rtl" as const,
					mode: "point" as const,
					intersect: true,
					callbacks: {
						title: () => "",
						label: function (context: any) {
							if (context.parsed.y === null) {
								return "";
							}
							return `בילירובין: ${context.parsed.y.toFixed(1)}, גיל: ${context.parsed.x}`;
						},
					},
				},
				// Title is specific to each chart, added below
			},
			scales: {
				x: {
					type: "linear" as const,
					title: { display: true, text: "גיל בשעות" },
					ticks: { stepSize: 12 },
					max: 168, // Specific max for phototherapy
				},
				y: {
					type: "linear" as const,
					title: { display: true, text: "בילירובין (mg/dl)" },
					beginAtZero: true,
					ticks: { stepSize: 2 }, // Specific ticks for phototherapy
					max: 26, // Specific max for phototherapy
				},
			},
		};

		return {
			...commonChartOptions,
			plugins: {
				...commonChartOptions.plugins,
				title: {
					display: true,
					text: `גרף טיפול באור - ${
						phototherapyChartsTitleSuffix[getGenericCaseIndex(isWeek38Plus, hasRiskFactors)]
					}`,
				},
			},
		};
	}, [
		phototherapyFinalData.datasets.length,
		allInputsSatisfied,
		phototherapyPointDataset.length,
		isWeek38Plus,
		hasRiskFactors,
	]); // Dependencies for phototherapy options

	const butaniChartOptions = useMemo(() => {
		const commonChartOptions = {
			// Define common options inside useMemo or make it a separate memoized value
			responsive: true,
			aspectRatio: 1,
			plugins: {
				legend: {
					// Specific legend filtering for Butani
					display: true,
					labels: {
						filter: (legendItem: any) =>
							legendItem.datasetIndex < 3 ||
							(legendItem.datasetIndex === butaniFinalData.datasets.length - 1 &&
								allInputsSatisfied), // Show base lines and point if valid
						usePointStyle: true,
						pointStyle: "line",
					},
					position: "top" as const,
				},
				tooltip: {
					rtl: true,
					textDirection: "rtl" as const,
					mode: "index" as const,
					intersect: true,
					callbacks: {
						title: () => "",
						label: function (context: any) {
							if (context.parsed.y === null) {
								return "";
							}
							return `בילירובין: ${context.parsed.y.toFixed(1)}, גיל: ${context.parsed.x}`;
						},
					},
				},
				title: { display: true, text: "עקומת בוטאני" }, // Title specific to Butani
			},
			scales: {
				x: {
					type: "linear" as const,
					title: { display: true, text: "גיל בשעות" },
					ticks: { stepSize: 12 },
					max: 144, // Specific max for Butani
				},
				y: {
					type: "linear" as const,
					title: { display: true, text: "בילירובין (mg/dl)" },
					beginAtZero: true,
					ticks: { stepSize: 5 }, // Specific ticks for Butani
					max: 25, // Specific max for Butani
				},
			},
		};
		return commonChartOptions; // Butani options are derived directly
	}, [butaniFinalData.datasets.length, allInputsSatisfied, butaniPointDataset.length]); // Dependencies for Butani options

	// --- Sub-components moved outside ---

	// Removed unused openGraphView function

	const closeSubView = useCallback(() => {
		setDataShown("CALCULATOR");
		// Removed calls to setters for unused state variables
	}, [setDataShown]); // Dependency: setDataShown (stable)

	// --- Main Render ---
	// (Rest of the component remains the same)

	console.log("dataShown", dataShown);

	return (
		<div className="app-with-footer">
			<div className="container main-content phototherapy-main-content" data-page-title={pageTitle}>
				{dataShown === "CALCULATOR" && (
					<div id="calculator">
						<AppsHeader title="צהבת ביילוד" />
						<Card
							radius="sm"
							className="w-full"
							style={{
								borderRadius: "var(--radius-m, 8px)",
								background: "var(--neutral-light, #EEFFFD)",
								boxShadow:
									"0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)",
							}}
						>
							<CardBody className="space-y-4">
								<div
									style={{
										width: "100%",
										maxWidth: "380px",
										marginInline: "auto",
									}}
								>
									<table
										style={{
											width: "100%",
											borderCollapse: "separate",
											borderSpacing: "0",
										}}
									>
										<tbody>
											{/* Week of birth */}
											<tr>
												<td
													style={{
														width: "25%",
														direction: "rtl",
														verticalAlign: "middle",
													}}
												>
													<label
														data-testid="week-of-birth-select"
														className="grid-label"
														style={labelStyle}
													>
														שבוע לידה
													</label>
												</td>
												<td style={{ direction: "rtl" }} colSpan={3}>
													<div
														role="radiogroup"
														aria-label="week-of-birth"
														style={{
															display: "flex",
															gap: "8px",
															width: "100%",
														}}
													>
														{(
															[
																{ key: "above38", label: "+38" },
																{ key: "under38", label: "35-37" },
															] as const
														).map((opt) => {
															const checked = weekOfBirth === opt.key;
															return (
																<button
																	key={opt.key}
																	type="button"
																	role="radio"
																	aria-checked={checked}
																	data-testid={`week-of-birth-${opt.key}`}
																	onClick={() =>
																		setWeekOfBirth(opt.key as WeekOfBirth)
																	}
																	style={{
																		flex: 1,
																		minHeight: "40px",
																		borderRadius: "8px",
																		border: checked
																			? "2px solid #103C6E"
																			: "1px solid #3b82f6",
																		background: checked
																			? "#103C6E"
																			: "white",
																		color: checked ? "#FFFFFF" : "#0C3058",
																		fontWeight: 500,
																		cursor: "pointer",
																		transition:
																			"background 120ms, color 120ms, border-color 120ms",
																	}}
																>
																	{opt.label}
																</button>
															);
														})}
													</div>
												</td>
											</tr>
											<tr>
												<td colSpan={4} style={{ height: "12px" }} />
											</tr>

											{/* Risk factor */}
											<tr>
												<td
													style={{
														direction: "rtl",
														verticalAlign: "middle",
													}}
												>
													<label className="grid-label" style={labelStyle}>
														גורם סיכון
													</label>
												</td>
												<td style={{ direction: "rtl" }} colSpan={3}>
													<div
														style={{
															display: "flex",
															alignItems: "center",
															gap: "10px",
														}}
													>
														<Checkbox
															data-testid="risk-factor-risky"
															isSelected={hasRiskFactors}
															onValueChange={setHasRiskFactors}
														/>
														<FaInfoCircle
															style={{
																cursor: "pointer",
																verticalAlign: "middle",
																color: "#0C3058",
															}}
															onClick={() => setDataShown("RISKS")}
															aria-label="Show risk factors info"
														/>
														<span style={{ color: "#0C3058" }}>
															לפירוט גורמי סיכון
														</span>
													</div>
												</td>
											</tr>
											<tr>
												<td colSpan={4} style={{ height: "12px" }} />
											</tr>

											{/* Bilirubin */}
											<tr>
												<td
													style={{
														direction: "rtl",
														verticalAlign: "middle",
													}}
												>
													<label className="grid-label" style={labelStyle}>
														בילירובין
													</label>
												</td>
												<td style={{ direction: "rtl" }} colSpan={3}>
													<Input
														id="bilirubin"
														placeholder="mg/dl"
														type="text"
														inputMode="decimal"
														pattern="[0-9]{0,2}[.,]?[0-9]{0,1}"
														value={bilirubin}
														onChange={handleBilirubinChange}
														onFocus={(e) => e.target.select()}
														classNames={{
															inputWrapper: `
                                                                bg-white border rounded-sm
                                                                hover:border-blue-600 focus-within:border-blue-700
                                                                border-blue-500
                                                            `,
															input: "bg-white text-right",
														}}
													/>
												</td>
											</tr>
											<tr>
												<td colSpan={4} style={{ height: "12px" }} />
											</tr>

											{/* Age */}
											<tr>
												<td
													style={{
														direction: "rtl",
														verticalAlign: "middle",
													}}
												>
													<label className="grid-label" style={labelStyle}>
														גיל
													</label>
												</td>
												<td style={{ direction: "rtl" }} colSpan={3}>
													<Input
														id="ageInHours"
														placeholder="שעות"
														type="text"
														inputMode="numeric"
														pattern="[0-9]{0,3}"
														value={ageInHours}
														onChange={handleAgeChange}
														onFocus={(e) => e.target.select()}
														classNames={{
															inputWrapper: `
                                                                bg-white border rounded-sm
                                                                hover:border-blue-600 focus-within:border-blue-700
                                                                border-blue-500
                                                            `,
															input: "bg-white text-right",
														}}
													/>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</CardBody>
						</Card>
						{allInputsSatisfied && (
							<div id="results-container" className="my-4 max-w-xl">
								<div className="flex items-center gap-2 mb-3">
									<Alert
										className="flex-1"
										hideIcon
										color={statusColor === "green" ? "success" : "danger"}
										style={{
											backgroundColor: statusColor === "green" ? "#E7FFDA" : undefined,
										}}
									>
										<span id="root-diagnose" style={{ color: "#2D598B" }}>{rootDiagnose}</span>
									</Alert>
									<button
										onClick={resetAll}
										className="p-1 hover:opacity-70 transition-opacity flex-shrink-0"
										aria-label="Reset"
										style={{ background: "transparent", border: "none", cursor: "pointer" }}
									>
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M1 4.00001V10M1 10H7M1 10L5.64 5.64001C6.71475 4.56473 8.04437 3.77922 9.50481 3.35679C10.9652 2.93436 12.5089 2.88877 13.9917 3.22427C15.4745 3.55978 16.8482 4.26545 17.9845 5.27543C19.1209 6.28542 19.9828 7.5668 20.49 9.00001M23 20V14M23 14H17M23 14L18.36 18.36C17.2853 19.4353 15.9556 20.2208 14.4952 20.6432C13.0348 21.0657 11.4911 21.1113 10.0083 20.7758C8.52547 20.4402 7.1518 19.7346 6.01547 18.7246C4.87913 17.7146 4.01717 16.4332 3.51 15" stroke="#103C6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
										</svg>
									</button>
								</div>
								<div>
									{lightAlertMessage && (
										<h5 id="light-alert-message" className="card-title text-warning">
											{lightAlertMessage}
										</h5>
									)}{" "}
									{/* Use warning color */}
									{riskZoneSatisfied && (
										<p id="risk-zone-full-text" className="card-text">
											היילוד <span>{riskZoneObj.percentileString}</span> באיזור סיכון{" "}
											<span>{riskZoneObj.riskZone}</span>.
											<br />
											<span>{riskZoneObj.explanation}</span>.
										</p>
									)}
									{calculatedThresholds && !isNaN(calculatedThresholds.photo) && (
										<p
											id="thresholds-info"
											style={{ fontSize: "1rem", marginTop: "8px" }}
										>
											עבור יילוד בגיל {ageInHoursNum} שעות, סף טיפול באור הוא{" "}
											{formatThresholdNum(calculatedThresholds.photo)}
											{!isNaN(calculatedThresholds.transfusion) && (
												<>
													{" "}
													וסף החלפת דם הוא{" "}
													{formatThresholdNum(calculatedThresholds.transfusion)}
												</>
											)}{" "}
											בילירובין.
										</p>
									)}
									{considerTransfusion && (
										<h4 id="consider-transfusion" className="card-title text-danger">
											{considerTransfusion}
										</h4>
									)}{" "}
									{/* Use danger color */}
									{shouldShowFollowUp && (
										<div className="card-text mt-3 follow-up-info">
											<strong>ילדים להם נמליץ על מעקב במרפאתינו ולא בקהילה:</strong>
											<ul>
												<li>פג, משבוע 36+6/7 ומטה בלידתו עם גורם סיכון</li>
												<li>יילוד המשתחרר פחות מ-8 שעות לאחר טיפול באור</li>
												<li>יילוד עם DAT חיובי הזקוק למעקב</li>
												<li>יילוד (או בן משפחה) עם חסר ב G6PD הזקוק למעקב </li>
											</ul>
										</div>
									)}
									<div className="mt-3">
										<button
											type="button"
											className="btn btn-link"
											style={{ padding: "0px" }}
											onClick={() => setIsCollapsed(!isCollapsed)}
										>
											{isCollapsed ? "צפה בערך על העקומות" : "סגור תצוגה גרפית"}
										</button>
									</div>
									{/* Adding auto scroll to ref -- chartsContainerRef */}
									{!isCollapsed && (
										<div ref={chartsContainerRef} className="charts-container mt-3">
											<div style={{ marginBottom: "20px" }}>
												<Line
													ref={phototherapyChartRef}
													options={phototherapyChartOptions}
													data={phototherapyFinalData}
												/>
											</div>
											<div>
												<Line
													ref={butaniChartRef}
													options={butaniChartOptions}
													data={butaniFinalData}
												/>
											</div>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Render sub-views based on dataShown */}
				{dataShown === "RISKS" && <RisksView onClose={closeSubView} />}
				{dataShown === "PHOTOTHERAPY_GRAPH" && (
					<GraphView
						onClose={closeSubView}
						title="גרף טיפול באור"
						path38="/assets/apps/phototherapy/phototherapy-treatment-graph.png"
						path37={null} // Only one graph for phototherapy treatment? Check original app if needed.
					/>
				)}
				{dataShown === "EXCHANGE_TRANSFUSION_GRAPH" && (
					<GraphView
						onClose={closeSubView}
						title="גרף החלפת דם"
						path38="/assets/apps/phototherapy/exchange-transfusion-38.png"
						path37="/assets/apps/phototherapy/exchange-transfusion-under-38.png"
					/>
				)}
				{dataShown === "GRAPH_BUTANI" && (
					<GraphView
						onClose={closeSubView}
						title="עקומת בוטאני"
						path38="/assets/apps/phototherapy/butani-updated.png"
						path37={null} // Only one graph for Butani
					/>
				)}
			</div>
			{/* fixed to botton */}
				<Footer>
					<FooterItem
						label="מחשבון צהבת"
						icon={faBaby}
						onClick={() => setDataShown("CALCULATOR")}
						isActive={dataShown === "CALCULATOR"}
					/>
					<FooterItem
						label="גורמי סיכון"
						icon={faBedPulse}
						onClick={() => setDataShown("RISKS")}
						isActive={dataShown === "RISKS"}
					/>
					<FooterItem
						label="גרף טיפול באור"
						icon={faChartLine}
						onClick={() => setDataShown("PHOTOTHERAPY_GRAPH")} // Directly set view
						isActive={dataShown === "PHOTOTHERAPY_GRAPH"}
					/>
					<FooterItem
						label="גרף החלפת דם"
						icon={faDroplet}
						onClick={() => setDataShown("EXCHANGE_TRANSFUSION_GRAPH")} // Directly set view
						isActive={dataShown === "EXCHANGE_TRANSFUSION_GRAPH"}
					/>
					<FooterItem
						label="עקומת בוטאני"
						icon={faChartGantt}
						onClick={() => setDataShown("GRAPH_BUTANI")} // Directly set view
						isActive={dataShown === "GRAPH_BUTANI"}
					/>
				</Footer>
		</div>
	);
};

// --- Sub-components defined outside main component and memoized ---

const RisksView: React.FC<{ onClose: () => void }> = React.memo(({ onClose }) => (
	<div className="content-risks">
		<h4 className="risks-header">גורמי סיכון בצהבת</h4>
		<ul className="list-group list-group-flush risks-container">
			<li className="list-group-item">המוליזה על רקע אימוני (direct antibody titer חיובי)</li>
			<li className="list-group-item">חוסר באנזים G-6-PD</li>
			<li className="list-group-item">תשניק פרינטלי</li>
			<li className="list-group-item">
				גיל הריון 37<span style={{ fontSize: "12px", verticalAlign: "top" }}>+6</span> שבועות או
				פחות
			</li>
			<li className="list-group-item">היפואלבומינמיה (אלבומין מתחת 3 גר'\ ד"ל)</li>
			<li className="list-group-item">תינוק במשפחה שטופל באור בעבר</li>
			<li className="list-group-item">דימומים תת-עוריים נרחבים</li>
			<li className="list-group-item">הנקה לא מספקת</li>
			<li className="list-group-item">ירידה ניכרת במשקל</li>
			<li className="list-group-item">תינוק לאם סוכרתית (מקרוזומי)</li>
		</ul>
	</div>
));
RisksView.displayName = "RisksView"; // Add display name for React DevTools

const GraphView: React.FC<{
	onClose: () => void;
	title: string;
	path38: string | null;
	path37: string | null;
}> = React.memo(({ onClose, title, path38, path37 }) => (
	<div className="content-risks">
		{" "}
		{/* Reusing some styles */}
		<h4 className="risks-header">{title}</h4> {/* Dynamic title */}
		{path38 && (
			<div style={{ marginTop: "2vh", backgroundColor: "white", position: "relative" }}>
				{/* Image src path relative to src/assets/ */}
				<Image src={path38} alt={`${title} Graph (>=38 weeks)`} width="100%" />
			</div>
		)}
		{path37 && (
			<div
				style={{
					marginTop: "2vh",
					marginBottom: "2vh",
					backgroundColor: "white",
					position: "relative",
				}}
			>
				<Image src={path37} alt={`${title} Graph (35-37 weeks)`} width="100%" />
			</div>
		)}
	</div>
));
GraphView.displayName = "GraphView"; // Add display name for React DevTools

export default PhototherapyIsraeliCurves;
